import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { useFormik } from 'formik';
import * as chanceApi from '../../../lib/api/chance';
import { setChance } from '../../../modules/chanceModule/chance';
import ProjectForm from '../../../components/chancemng/ProjectForm';
import DetailTemplate from '../../../components/common/template/DetailTemplate';
import useFieldConfig from '../../../hooks/useFieldConfig';

const ChanceConfirmFormContainer = ({ stepperRef }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  
  // 필드 설정 조회
  const { isFieldEnabled, isFieldRequired, loading: fieldConfigLoading } = useFieldConfig('CHANCE');
  
  const { chance } = useSelector(({ chanceModule }) => ({
    chance: chanceModule.chance.chance,
  }));

  let initialValues = {
    cstNm:
      chance && chance.cstNm !== null && chance.cstNm !== undefined
        ? chance.cstNm
        : '',
    partnerInc:
      chance && chance.partnerInc !== null && chance.partnerInc !== undefined
        ? chance.partnerInc
        : '',
    customerUuid:
      chance && chance.customerUuid !== null && chance.customerUuid !== undefined
        ? chance.customerUuid
        : '',
    vendorCmp:
      chance && chance.vendorCmp !== null && chance.vendorCmp !== undefined
        ? chance.vendorCmp
        : '',
    vendorEmp:
      chance && chance.vendorEmp !== null && chance.vendorEmp !== undefined
        ? chance.vendorEmp
        : '',
    prjTyp:
      chance && chance.prjTyp !== null && chance.prjTyp !== undefined
        ? chance.prjTyp
        : '',
    incTyp:
      chance && chance.incTyp !== null && chance.incTyp !== undefined
        ? chance.incTyp
        : '',
    solutionTyp:
      chance && chance.solutionTyp !== null && chance.solutionTyp !== undefined
        ? chance.solutionTyp
        : '',
    chanceTyp:
      chance && chance.chanceTyp !== null && chance.chanceTyp !== undefined
        ? chance.chanceTyp
        : '',
    prjImpt:
      chance && chance.prjImpt !== null && chance.prjImpt !== undefined
        ? chance.prjImpt
        : '',
    bugetYn:
      chance && chance.bugetYn !== null && chance.bugetYn !== undefined
        ? chance.bugetYn
        : '',
    procurement: chance && chance.procurement === 'Y' ? true : false,
    partnerTyp:
      chance && chance.partnerTyp !== null && chance.partnerTyp !== undefined
        ? chance.partnerTyp
        : '',
    chargeNm:
      chance && chance.chargeNm !== null && chance.chargeNm !== undefined
        ? chance.chargeNm
        : '',
    chargeEml:
      chance && chance.chargeEml !== null && chance.chargeEml !== undefined
        ? chance.chargeEml
        : '',
    chargeTel:
      chance && chance.chargeTel !== null && chance.chargeTel !== undefined
        ? chance.chargeTel
        : '',
    forcastTxt:
      chance && chance.forcastTxt !== null && chance.forcastTxt !== undefined
        ? chance.forcastTxt
        : '',
  };

  // 동적 validation schema 생성
  const validationSchema = useMemo(() => {
    const schema = {};

    // 각 필드별 validation 정의
    const fieldValidations = {
      cstNm: { 
        base: yup.string(), 
        requiredMsg: '사업명을 입력하세요' 
      },
      prjTyp: { 
        base: yup.string(), 
        requiredMsg: '프로젝트 종류를 선택하세요' 
      },
      partnerInc: { 
        base: yup.string(), 
        requiredMsg: '고객명을 입력하세요' 
      },
      incTyp: { 
        base: yup.string(), 
        requiredMsg: '거래처 유형을 선택하세요' 
      },
      solutionTyp: { 
        base: yup.string().nullable(), 
        requiredMsg: '아이템 구분을 선택하세요' 
      },
      chanceTyp: { 
        base: yup.string(), 
        requiredMsg: '기회유형을 선택하세요' 
      },
      prjImpt: { 
        base: yup.string(), 
        requiredMsg: '프로젝트 중요도를 선택하세요' 
      },
      bugetYn: { 
        base: yup.string(), 
        requiredMsg: '예산여부를 선택하세요' 
      },
      chargeNm: { 
        base: yup.string(), 
        requiredMsg: '고객 담당자를 입력하세요' 
      },
      chargeEml: { 
        base: yup.string().email('이메일 형식을 확인하세요').nullable(), 
        requiredMsg: '이메일을 입력하세요' 
      },
      chargeTel: { 
        base: yup.string().nullable(), 
        requiredMsg: '연락처를 입력하세요' 
      },
      forcastTxt: { 
        base: yup.string().nullable(), 
        requiredMsg: '참고사항을 입력하세요' 
      },
    };

    // 활성화된 필드만 validation에 추가
    Object.keys(fieldValidations).forEach((fieldCd) => {
      if (isFieldEnabled(fieldCd)) {
        const { base, requiredMsg } = fieldValidations[fieldCd];
        schema[fieldCd] = isFieldRequired(fieldCd) 
          ? base.required(requiredMsg) 
          : base;
      }
    });

    return yup.object(schema);
  }, [isFieldEnabled, isFieldRequired]);

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      onSave(values);
      formik.resetForm();
      setSubmitting(false);
      stepperRef.current?.scrollIntoView({ behavior: 'smooth' }); //저장 후 stepper로 이동함.
    },
  });

  const onSave = async (values) => {
    let dataToSubmit = {
      prjUuid: chance.prjUuid,
      partnerInc: values.partnerInc,
      vendorCmp: values.vendorCmp || values.customerUuid,
      vendorEmp: values.vendorEmp,
      cstNm: values.cstNm,
      prjTyp: values.prjTyp,
      incTyp: values.incTyp,
      solutionTyp: values.solutionTyp,
      chanceTyp: values.chanceTyp,
      prjImpt: values.prjImpt,
      bugetYn: values.bugetYn,
      partnerTyp: values.partnerTyp,
      chargeNm: values.chargeNm,
      chargeEml: values.chargeEml,
      chargeTel: values.chargeTel,
      forcastTxt: values.forcastTxt,
      procurement: values?.procurement ? 'Y' : 'N',
    };

    //  console.log(dataToSubmit,'data');

    try {
      const res = await chanceApi.editChance(dataToSubmit);
      if (res.data) {
        dispatch(setChance(res.data.info));
        enqueueSnackbar(`세부 저장 성공`, {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  return (
    <>
      {chance?.useYn === 'N' && (
        <DetailTemplate title="포캐스팅 실패 사유">
          <Box sx={{ whiteSpace: 'pre-line' }}>{chance?.failTxt}</Box>
        </DetailTemplate>
      )}
      <ProjectForm 
        formik={formik} 
        isFieldEnabled={isFieldEnabled}
        isFieldRequired={isFieldRequired}
      />
    </>
  );
};

export default ChanceConfirmFormContainer;
